import React from 'react';
import { formatAmount } from '../utils/formatters';

interface PreSaveTransactionSummaryProps {
  stats: {
    totalTransactions: number;
    totalAmount: number;
    totalSiteFees: number;
    totalBankFees: number;
    totalResellerFees: number;
    approvedTransactions: number;
    declinedTransactions: number;
    returnedTransactions: number;
  };
  dateRange: string;
  queryDateRange?: {
    start: Date;
    end: Date;
  };
}

export function PreSaveTransactionSummary({ stats, dateRange, queryDateRange }: PreSaveTransactionSummaryProps) {
  // Ensure all values have defaults
  const totalTransactions = stats.totalTransactions || 0;
  const totalAmount = stats.totalAmount || 0;
  const totalSiteFees = stats.totalSiteFees || 0;
  const totalBankFees = stats.totalBankFees || 0;
  const totalResellerFees = stats.totalResellerFees || 0;
  const approvedTransactions = stats.approvedTransactions || 0;
  const declinedTransactions = stats.declinedTransactions || 0;
  const returnedTransactions = stats.returnedTransactions || 0;
  
  // Calculate net revenue
  const netRevenue = totalSiteFees - totalBankFees - totalResellerFees;
  
  // Calculate percentages safely
  const getPercentage = (value: number) => {
    return totalTransactions > 0 ? (value / totalTransactions) * 100 : 0;
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h1 className="text-xl font-semibold text-gray-900">Dataset Summary</h1>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Transaction Volume */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500">Transaction Volume</h3>
            <div className="mt-2">
              <p className="text-2xl font-semibold text-gray-900">
                {formatAmount(totalAmount)}
              </p>
              <p className="text-sm text-gray-500">
                {totalTransactions.toLocaleString()} transactions
              </p>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500">Status Distribution</h3>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Approved</span>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {approvedTransactions.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({getPercentage(approvedTransactions).toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Declined</span>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {declinedTransactions.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({getPercentage(declinedTransactions).toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Returned</span>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {returnedTransactions.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({getPercentage(returnedTransactions).toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Fee Breakdown */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500">Fee Breakdown</h3>
            <div className="mt-2 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Site Fees</span>
                <span className="text-sm font-medium text-gray-900">{formatAmount(totalSiteFees)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Bank Fees</span>
                <span className="text-sm font-medium text-gray-900">{formatAmount(totalBankFees)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Reseller Fees</span>
                <span className="text-sm font-medium text-gray-900">{formatAmount(totalResellerFees)}</span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Net Revenue</span>
                  <span className="text-sm font-medium text-green-600">
                    {formatAmount(netRevenue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500">Date Range</h3>
            <div className="mt-2">
              <p className="text-lg font-medium text-gray-900">
                {dateRange === '3days' ? 'Last 3 Days' : 
                 dateRange === '1month' ? 'Last Full Month' :
                 'Custom Range'}
              </p>
              {queryDateRange && (
                <p className="mt-1 text-sm text-gray-500">
                  {queryDateRange.start.toLocaleDateString()} - {queryDateRange.end.toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}